import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import type RealtimeKitClient from "@cloudflare/realtimekit";
import {
  RtkMeeting,
  provideRtkDesignSystem,
  useLanguage as createRtkI18n,
} from "@cloudflare/realtimekit-react-ui";
import { rtkLanguagePtBr } from "./rtkLanguagePtBr";
import {
  applyJoinNotificationsOff,
  buildMeetingUiConfig,
} from "./rtkSuppressJoinNotifications";
import { enableDyteNameTagReadability } from "./dyteNameTagReadability";
import {
  closeRtkSidebar,
  isRtkSidebarOpen,
  readActiveSidebarFromEvent,
  RTK_STATES_EVENTS,
} from "./rtkSidebar";

const rtkMeetingUiConfig = buildMeetingUiConfig();

// Inicializa o design system uma vez.
if (typeof document !== "undefined") {
  provideRtkDesignSystem(document.documentElement, { theme: "dark" });
}

// Tradutor pt-BR para toda a interface da sala (botões, avisos, notificações).
const tPtBr = createRtkI18n(rtkLanguagePtBr);

interface DyteMeetProps {
  meeting: RealtimeKitClient;
  /**
   * Encaminha o evento `roomLeft` do RealtimeKit com o `state` original
   * ('left' | 'ended' | 'kicked' | 'rejected' | 'disconnected' | 'failed' ...).
   */
  onMeetingEnd?: (state?: string) => void;
  onSidebarOpenChange?: (open: boolean) => void;
  closeSidebarRef?: React.MutableRefObject<(() => void) | null>;
}

const DyteMeet: React.FC<DyteMeetProps> = ({
  meeting,
  onMeetingEnd,
  onSidebarOpenChange,
  closeSidebarRef,
}) => {
  const meetingEl = useRef<HTMLRtkMeetingElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const onSidebarOpenChangeRef = useRef(onSidebarOpenChange);
  onSidebarOpenChangeRef.current = onSidebarOpenChange;

  const bindMeetingElement = useCallback(
    (el: HTMLRtkMeetingElement | null) => {
      meetingEl.current = el;
      if (el) {
        applyJoinNotificationsOff(el);
      }
    },
    [],
  );

  useLayoutEffect(() => {
    const el = meetingEl.current;
    if (!el || !meeting) {
      return;
    }

    // O RtkMeeting força loadConfigFromPreset=true e sobrescreve a config ao montar
    // a meeting — por isso reaplicamos várias vezes (inclui reconexão / rejoin).
    const apply = () => applyJoinNotificationsOff(el);

    apply();
    const t1 = window.setTimeout(apply, 0);
    const t2 = window.setTimeout(apply, 100);
    const t3 = window.setTimeout(apply, 400);

    const onRoomJoined = () => apply();
    meeting.self.on("roomJoined", onRoomJoined);

    const onStates = (event: Event) => {
      const open = readActiveSidebarFromEvent(event);
      if (typeof open === "boolean") {
        onSidebarOpenChangeRef.current?.(open);
      }
    };
    for (const type of RTK_STATES_EVENTS) {
      el.addEventListener(type, onStates);
    }

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      meeting.self.removeListener("roomJoined", onRoomJoined);
      for (const type of RTK_STATES_EVENTS) {
        el.removeEventListener(type, onStates);
      }
    };
  }, [meeting]);

  useLayoutEffect(() => {
    const handleRoomLeft = ({ state }: { state?: string }) => {
      onMeetingEnd?.(state);
    };

    meeting.self.on("roomLeft", handleRoomLeft);

    return () => {
      meeting.self.removeListener("roomLeft", handleRoomLeft);

      if (meeting.stage && typeof meeting.stage.removeAllListeners === "function") {
        meeting.stage.removeAllListeners("stageStatusUpdate");
      }
    };
  }, [meeting, onMeetingEnd]);

  useEffect(() => {
    return enableDyteNameTagReadability(wrapperRef.current);
  }, [meeting]);

  useEffect(() => {
    const close = () => {
      closeRtkSidebar(wrapperRef.current);
    };
    if (closeSidebarRef) {
      closeSidebarRef.current = close;
    }
    return () => {
      if (closeSidebarRef && closeSidebarRef.current === close) {
        closeSidebarRef.current = null;
      }
    };
  }, [closeSidebarRef, meeting]);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) {
      return;
    }

    const publish = (open?: boolean) => {
      onSidebarOpenChangeRef.current?.(
        typeof open === "boolean" ? open : isRtkSidebarOpen(root),
      );
    };

    const onStates = (event: Event) => {
      const open = readActiveSidebarFromEvent(event);
      if (typeof open === "boolean") {
        publish(open);
        return;
      }
      publish();
    };

    for (const type of RTK_STATES_EVENTS) {
      root.addEventListener(type, onStates);
    }

    const observer = new MutationObserver(() => publish());
    observer.observe(root, { childList: true, subtree: true, attributes: true });
    const intervalId = window.setInterval(() => publish(), 800);
    publish();

    return () => {
      for (const type of RTK_STATES_EVENTS) {
        root.removeEventListener(type, onStates);
      }
      observer.disconnect();
      window.clearInterval(intervalId);
    };
  }, [meeting]);

  return (
    <div ref={wrapperRef} className="h-full w-full relative">
      <RtkMeeting
        meeting={meeting}
        ref={bindMeetingElement}
        t={tPtBr}
        config={rtkMeetingUiConfig}
        showSetupScreen={false}
        className="w-full h-full"
        leaveOnUnmount={false}
        mode="fill"
      />
    </div>
  );
};

export default DyteMeet;
